<template>
  <div class="graphview">
    <vk-card class="graph-card">
      <h1 style="margin-top: 0; margin-bottom: 16px;">Grafico</h1>

      <template v-if="queryLastResult != null && queryLastResult.rows != null && queryLastResult.rows.length > 0">
        <select
          class="uk-select uk-form-width-large"
          v-model="currentDeviceID"
          @change="() => { currentDeviceChanged(currentDeviceID) }"
        >
          <option
            v-for="device in $store.state.userDevices"
            :key="device.id"
            :value="device.id"
          >{{ device.label }}</option>
        </select>
      </template>
      <template v-else>
        Nessun record nella query specificata: usare il pannello in basso a sinistra dell'interfaccia per specificare un periodo e dei device,
        quindi premere "Aggiorna" per effettuare la query (i cui risultati saranno visibili in questa schermata, nel tabulato e come percorsi nella mappa).
      </template>

      <br><br>
      
      <div class="uk-margin">
        <label class="uk-form-label">Serie visibili</label>
        <br>
        <template v-for="col in columns">
          <div v-bind:key="col" class="graph-column-checkbox">
            <input v-model="enabledColumns[col]" class="uk-checkbox" type="checkbox" @change="updateGraph"> {{ queryLastResult.columns[col].label }}
          </div>
        </template>
      </div>
      
      <canvas id="chart1-id" width="320" height="150"></canvas>
    </vk-card>
  </div>
</template>

<style lang="scss">
  .graph-card {
    height: 100%;
  }

  .graph-card > h1 {
    padding: 0;
  }

  .graph-column-checkbox {
    display: inline-block;
    padding-right: 1em;
  }
</style>

<script>
import Chart from 'chart.js'
import { mapState } from 'vuex'
import palette from 'google-palette'

import config from '@/config'
import { decimalToHex } from '@/utils'

export default {
  name: "graphview",

  components: {},

  beforeCreate() {
    if (!this.$store.state.isLogged) {
      this.$router.push("/login")
    }
  },

  data() {
    return {
      chart: null,
      currentDeviceID: this.$store.state.currentGraphDeviceID,
      columns: [],
      enabledColumns: {}
    }
  },

  mounted() {
    this.updateColumns()

    let context = document.getElementById("chart1-id").getContext("2d")

    this.chart = new Chart(context, {
      type: "line",
      data: {
        labels: [],
        datasets: []
      },
      options: {
        scales: {
          yAxes: [],
          xAxes: [
            {
              type: 'time',
              display: true
            }
          ]
        },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              // do nothing for normal columns
              if (data.datasets[tooltipItem.datasetIndex].isErrorIndicator == false) {
                var label = data.datasets[tooltipItem.datasetIndex].label || '';
                if (label) {
                  label += ': '
                }
                label += tooltipItem.yLabel
                return label
              } else {
                const index = tooltipItem.index
                const errorBitsColumns = data.datasets[tooltipItem.datasetIndex].errorBitsColumns
                const queryLastResult = data.datasets[tooltipItem.datasetIndex].queryLastResult
                const records = data.datasets[tooltipItem.datasetIndex].records
                const indicatorValue = tooltipItem.yLabel

                if (indicatorValue != 0) {
                  let label = "[ALLERTA]"

                  errorBitsColumns.forEach((col) => {
                    const column = queryLastResult.columns[col]
                    const value = records[index][col]

                    label += " " + column.label + "=0x" + decimalToHex(value, 4)
                  })
                  
                  return label
                } else {
                  return "[nessuna allerta]"
                }
              }
            }
          }
        },
        hover: {
          mode: "nearest",
          intersect: true,
          animationDuration: 0 // [performance] disable animations
        },
        elements: {
          point: {
            radius: 0
          },
          line: {
            tension: 0 // [performance] disable bezier curves
          }
        },
        animation: {
            duration: 0 // [performance] disable animations
        },
        responsiveAnimationDuration: 0 // [performance] disable animations
      }
    })

    this.updateGraph()
  },

  computed: mapState(['queryLastResult', 'currentGraphDeviceID']),

  watch: {
    queryLastResult () {
      this.updateGraph()
      this.updateColumns()
    },

    currentGraphDeviceID (deviceID) {
      this.currentDeviceID = this.currentGraphDeviceID
      this.currentDeviceChanged(deviceID)
    }
  },

  methods: {
    currentDeviceChanged () {
      this.$store.commit('SET_CURRENT_GRAPH_DEVICE', this.currentDeviceID)
      this.updateGraph()
    },

    updateGraph () {
      let errorBitsColumns = []
      for (let col in this.queryLastResult.columns) {
        const column = this.queryLastResult.columns[col]
        if (column.type == "errorbits") {
          errorBitsColumns.push(col)
        }
      }

      let records = this.queryLastResult.rows.filter((row) => row[2] == this.currentDeviceID)
      records.reverse()

      const colors = palette('mpn65', this.queryLastResult.columns.length).map((hex) => '#' + hex)
      
      this.chart.data.labels = records.map((row) => new Date(row[1] * 1000))
      
      this.chart.options.scales.yAxes = []
      this.chart.data.datasets = []
      for (let col in this.queryLastResult.columns) {
        if (this.enabledColumns[col] == true) {
          const column = this.queryLastResult.columns[col]
          const axisID = 'axis-y-' + col

          let axesConfig = {
            id: axisID,
            ticks: {
              beginAtZero: false
            },
            type: 'linear',
            display: false
          }

          if (column.type == "error") {
            axesConfig.ticks.min = 0
            axesConfig.ticks.max = 4
          }

          this.chart.options.scales.yAxes.push(axesConfig)

          let steppedLine = false
          if (column.type == "errorbits" || column.type == "error" || col == 6) {
            steppedLine = 'before'
          }

          let borderColor = colors[col]
          let backgroundColor = colors[col]
          let fill = false
          if (column.type == "error") {
            borderColor = 'rgba(255, 0, 0, 1)'
            backgroundColor = 'rgba(255, 0, 0, 0.4)'
            fill = true
          }

          this.chart.data.datasets.push({
            label: column.label,
            data: records.map((row) => row[col]),
            yAxisID: axisID,
            borderColor: borderColor,
            backgroundColor: backgroundColor,
            fill: fill,
            steppedLine: steppedLine,
            isErrorIndicator: (column.type == "error"),
            errorBitsColumns: (column.type == "error" ? errorBitsColumns : null),
            queryLastResult: (column.type == "error" ? this.queryLastResult : null),
            records: (column.type == "error" ? records : null)
          })
        }
      }

      this.chart.update()
    },

    updateColumns () {
      console.log("updating columns")

      let columns = []

      for (let col = 0; col < this.queryLastResult.columns.length; col++) {
        if (config.GRAPH_HIDDEN_COLUMNS.indexOf(col) > -1) {
          continue
        }

        columns.push(col)

        if (!(col in this.enabledColumns)) {
          if (config.GRAPH_DEFAULT_ENABLED_COLUMNS.indexOf(col) > -1) {
            this.enabledColumns[col] = true
          } else {
            this.enabledColumns[col] = false
          }
        }
      }

      let toDelete = []

      for (let col in this.enabledColumns) {
        if (!(col in columns)) {
          toDelete.push(col)
        }
      }

      for (let col in toDelete) {
        delete this.enabledColumns[col];
      }

      this.columns = columns
    }
  }
}
</script>
